import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CandidateDetailModal = ({ candidate, onClose, onStatusChange }) => {
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'communication', label: 'Communication', icon: 'MessageSquare' }
  ];

  const mockDocuments = [
    { name: 'Resume', file: candidate?.resume, uploadDate: '2026-01-15' },
    { name: 'Degree Certificate', file: 'degree_certificate.pdf', uploadDate: '2026-01-15' },
    { name: 'Experience Letter', file: 'experience_letter.pdf', uploadDate: '2026-01-15' }
  ];

  const mockCommunication = [
    { date: '2026-02-01', type: 'email', subject: 'Application Received', message: 'Thank you for applying...' },
    { date: '2026-02-02', type: 'note', subject: 'Internal Note', message: 'Strong technical skills, good fit for the role' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-heading font-bold text-card-foreground">Candidate Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex border-b border-border">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-smooth ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              {tab?.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                  <Image
                    src={candidate?.photo}
                    alt={candidate?.photoAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-card-foreground mb-2">{candidate?.name}</h3>
                  <p className="text-base text-muted-foreground mb-4">{candidate?.qualification}</p>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                      <span>{candidate?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Phone" size={16} color="var(--color-muted-foreground)" />
                      <span>{candidate?.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
                      <span>{candidate?.location}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-success mb-1">{candidate?.matchScore}%</div>
                  <p className="text-sm text-muted-foreground">Match Score</p>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-card-foreground mb-3">Experience</h4>
                <p className="text-base text-muted-foreground">{candidate?.experience}</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-card-foreground mb-3">Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {candidate?.skills?.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              {mockDocuments?.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    <Icon name="FileText" size={24} color="var(--color-primary)" />
                    <div>
                      <p className="text-sm font-medium text-card-foreground">{doc?.name}</p>
                      <p className="text-xs text-muted-foreground">Uploaded: {doc?.uploadDate}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'communication' && (
            <div className="space-y-4">
              {mockCommunication?.map((comm, index) => (
                <div key={index} className="p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name={comm?.type === 'email' ? 'Mail' : 'FileText'} size={16} color="var(--color-primary)" />
                    <span className="text-sm font-medium text-card-foreground">{comm?.subject}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{comm?.date}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{comm?.message}</p>
                </div>
              ))}
              <Button variant="default" iconName="Plus" iconPosition="left" fullWidth>
                Add Note
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} fullWidth>
            Close
          </Button>
          <Button
            variant="success"
            onClick={() => {
              onStatusChange(candidate?.id, 'Shortlisted');
              onClose();
            }}
            iconName="CheckCircle"
            iconPosition="left"
            fullWidth
          >
            Shortlist
          </Button>
          <Button
            variant="default"
            onClick={() => {
              onStatusChange(candidate?.id, 'Interview Scheduled');
              onClose();
            }}
            iconName="Calendar"
            iconPosition="left"
            fullWidth
          >
            Schedule Interview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CandidateDetailModal;